<script setup lang="ts">
import {
	type PropType,
	computed,
	ref,
	onMounted,
	provide,
	type VNodeRef,
} from 'vue'
import { useStore } from '@nanostores/vue'
import { useWindowScroll } from '@vueuse/core'
import PageBreadCrumb from '@/components/PageBreadCrumb.vue'
import BaseButton from '@/components/ui/button/BaseButton.vue'
import BaseIcon from '@/components/ui/icon/BaseIcon.vue'
import BackButton from '@/components/BackButton.vue'
import Skeleton from '@/components/Skeleton.vue'
import FeaturedBrandsSection from './FeaturedBrandsSection.vue'
import CombinedFlowSection from '@/components/CombinedFlowSection.vue'
import ShipmentProtectionFAQ from '@/pages/shipment-protection-shipping/components/_ShipmentProtectionFAQ.vue'
import ProductSharingPostList from '@/legacy/components/ProductSharingPostList.vue'
import BsProductSharingPostCard from '@/legacy/components/ProductSharingPostCard.vue'
import BsDialogAction from '@/legacy/components/DialogAction.vue'
import BsSnackBar from '@/legacy/components/SnackBar.vue'
import { getSiteBaseUrl } from '@/lib/siteBaseUrl'
import { getVueAssetImagePath, useBreakpoints } from '@/lib/utils'
import { isLogin } from '@/store/userStore'
import {
	fetchShippingGlobalPageData,
	type IShippingGlobalPageData,
} from '@/assets/ts/apiManagement'
import useSnackBar from '@/legacy/ts/useSnackBar'
import usePostCard from '@/legacy/ts/usePostCard'
import { fetchPricingCurrencyBuyforyou } from '@/legacy/ts/buyforyou'
import {
	checkExpressCheckoutEnable,
	checkProductSharingEnable,
} from '@/legacy/ts/productSharing'
import {
	createGAEvent,
	GaGeneralEvent,
	GaGeneralPageName,
} from '@/assets/ts/customEvent'

const { isLarge } = useBreakpoints()
const isLoginGlobal = useStore(isLogin)
const { addMessage, isShow, messages, dismiss } = useSnackBar()

const { y: scrollY } = useWindowScroll()

const isShowBackToTopButton = computed(() => {
	return scrollY.value > window.innerHeight * 0.33
})

// Provide required dependencies for ProductSharingPostList
provide('addMessage', addMessage)

const postListRef = ref<VNodeRef | null>(null)

const {
	postDetailCurrent,
	categoryMap,
	warehouseIdMap,
	interestsMap,
	supportedCurrencies,
	buyforyouSupportWarehouse,
	priceLocal,
	priceLocalPost,
	DialogType,
	isShowDialogPostDetail,
	isLoadingPostDetail,
	hideDialogPostDetail,
	setPostActions,
} = usePostCard(postListRef)

const props = defineProps({
	pageContent: {
		type: Object as PropType<{
			breadcrumbs: {
				[key: string]: string
			}
			hero: {
				title: string
				primaryBtn: string
				discountInfo: string
				stats: Array<{
					key: string
					description: string
				}>
			}
			comparison: {
				title: string
				description: string
				headers: string[]
				buttonText: string
				buttonLink: string
				protectionLabels: {
					[key: string]: string
				}
			}
			brands: {
				title: string
				description: string
				buttonText: string
				buttonLink: string
			}
			communityPosts: {
				title: string
				description: string
				buttonText: string
				buttonLink: string
			}
			postListText: any
			postCardTextInfo: any
			latestNews: {
				title: string
			}
			paymentsSection: {
				title: string
				imageList: string[]
			}
			warehousesSection: {
				title: string
			}
			combinedFlow: {
				sectionTitle: string
				buttonText: string
				tabs: Array<{
					id: 'parcel_forwarding' | 'proxy_shopping'
					title: string
					steps: Array<{
						title: string
						description: string
					}>
				}>
			}
			faq: {
				sectionTitle: string
			}
		}>,
		required: true,
	},
})

const pageData = ref<IShippingGlobalPageData | null>(null)
const isLoadingPageData = ref(false)
const localCurrencyInfo = ref<any>(null)
const isExpressCheckoutEnable = ref(checkExpressCheckoutEnable())
const isProductSharingEnable = ref(checkProductSharingEnable())

const textReplacements = computed(() => ({
	'{TARGET_COUNTRY}': pageData.value?.name || '',
	'{WELCOME_CREDIT}': window.BUYANDSHIP?.settings?.member_welcome_credits || '',
}))

// Computed property to replace placeholders with actual values
const localizedPageContent = computed(() => {
	const replacements = textReplacements.value

	const replaceInObject = (obj: any): any => {
		if (typeof obj === 'string') {
			let result = obj
			// Apply all replacements
			Object.entries(replacements).forEach(([placeholder, value]) => {
				if (value) {
					result = result.replace(new RegExp(placeholder, 'g'), value)
				}
			})
			return result
		}
		if (Array.isArray(obj)) {
			return obj.map(item => replaceInObject(item))
		}
		if (obj !== null && typeof obj === 'object') {
			return Object.entries(obj).reduce((result, [key, value]) => {
				result[key] = replaceInObject(value)
				return result
			}, {} as any)
		}
		return obj
	}

	return replaceInObject(props.pageContent)
})

const currentCountry = computed(() => {
	if (import.meta.env.DEV) {
		// Dev/Staging: use hash-based routing (/shipping-global#from-{country})
		const { hash } = window.location
		const hashMatch = hash.match(/#from-([^/]+)/)
		return hashMatch ? hashMatch[1] : null
	}
	// Prod: use path-based routing (any path ending with /from-{country})
	const { pathname } = window.location
	const pathMatch = pathname.match(/\/from-([^/]+)/)
	return pathMatch ? pathMatch[1] : null
})

const heroStats = computed(() => {
	const staticStats = localizedPageContent.value.hero.stats

	if (!pageData.value?.hero_section?.stats) {
		return staticStats.map(stat => ({
			value: '',
			description: stat.description,
		}))
	}

	const apiStats = pageData.value.hero_section.stats as Record<string, string>

	return staticStats.map(stat => ({
		value: apiStats[stat.key] || '',
		description: stat.description,
	}))
})

const comparisonRows = computed(() => {
	if (!pageData.value?.comparison_table) return []

	const { protectionLabels } = localizedPageContent.value.comparison

	return pageData.value.comparison_table.map(row => {
		// Transform the last item (protection type) using the label mapping
		const transformedData = row.data.map((cell, index) => {
			// If it's the last column and it's a protection key, translate it
			if (
				index === row.data.length - 1 &&
				protectionLabels[cell as keyof typeof protectionLabels]
			) {
				return protectionLabels[cell as keyof typeof protectionLabels]
			}
			return cell
		})

		return {
			company: row.company,
			isHighlight: row.is_highlighted,
			data: transformedData,
		}
	})
})

const breadcrumbs = computed(() => [
	{
		displayName: localizedPageContent.value.breadcrumbs.home,
		categoryId: 'home',
		link: getSiteBaseUrl(),
	},
	{
		displayName: localizedPageContent.value.breadcrumbs.shipping,
		categoryId: 'shipping',
		link: `${getSiteBaseUrl()}/shipping/`,
	},
	{
		displayName: localizedPageContent.value.breadcrumbs.shippingGlobal,
		categoryId: 'shippingGlobal',
		link: '',
	},
])

const faqContent = computed(() => {
	const faqItems =
		pageData.value?.faq?.map(item => ({
			question: item.question,
			isOpen: false,
			answer: {
				items: [
					{
						type: 'html' as const,
						content: [item.answer],
					},
				],
			},
		})) ?? []

	return {
		sectionTitle: localizedPageContent.value.faq.sectionTitle,
		questions: '',
		CTAButton: { text: '', link: '' },
		linkCards: [],
		faqItems,
	}
})

const columnCount = computed(() => (isLarge.value ? 4 : 2))

const latestNewsPosts = computed(() => {
	return pageData.value?.beginner_blogposts?.slice(0, 4) ?? []
})

const otherWarehouses = computed(() => {
	return pageData.value?.other_warehouses ?? []
})

const getFlagIcon = (warehouseId: string): string => {
	return getVueAssetImagePath(`country-flag-${warehouseId}.png`)
}

const imageLoadedStates = ref<Record<string, boolean>>({})

const onImageLoad = (imgUrl: string) => {
	imageLoadedStates.value[imgUrl] = true
}

const onImageError = (imgUrl: string) => {
	imageLoadedStates.value[imgUrl] = true
}

const handleRegisterClick = () => {
	createGAEvent(GaGeneralEvent.ButtonClick, {
		page_name: GaGeneralPageName.ShippingGlobal,
		button_name: 'Sign Up - Hero',
	})

	window.BsHeaderVm.showSignupPanel()
}

const handleComparisonButtonClick = () => {
	createGAEvent(GaGeneralEvent.ButtonClick, {
		page_name: GaGeneralPageName.ShippingGlobal,
		button_name: 'Pricing',
	})
}

const handleCommunityPostsButtonClick = () => {
	createGAEvent(GaGeneralEvent.ButtonClick, {
		page_name: GaGeneralPageName.ShippingGlobal,
		button_name: 'View More - Community Post Listing',
	})
}

const handleLatestNewsPostClick = (title: string) => {
	createGAEvent(GaGeneralEvent.ButtonClick, {
		page_name: GaGeneralPageName.ShippingGlobal,
		button_name: 'Select Blog Post',
		event_extra: title,
	})
}

const handleOtherWarehousesClick = (slug: string) => {
	createGAEvent(GaGeneralEvent.ButtonClick, {
		page_name: GaGeneralPageName.ShippingGlobal,
		button_name: 'Other Warehouses',
		event_extra: slug,
	})
}

const scrollToTop = () => {
	window.scrollTo({
		top: 0,
		behavior: 'smooth',
	})
}

const loadShippingGlobalPageData = async (country: string) => {
	if (!country) return

	isLoadingPageData.value = true
	try {
		pageData.value = await fetchShippingGlobalPageData(country)
	} catch (error) {
		console.error('Failed to fetch shipping global page data:', error)
	} finally {
		isLoadingPageData.value = false
	}
}

const setLocalCurrencyInfo = async () => {
	localCurrencyInfo.value = await fetchPricingCurrencyBuyforyou()
}

onMounted(async () => {
	// Local env could access this page by route /shipping-global#from-japan
	if (!currentCountry.value) {
		console.error('No available country from url')
		return
	}

	await Promise.all([
		loadShippingGlobalPageData(currentCountry.value),
		isExpressCheckoutEnable.value && setLocalCurrencyInfo(),
	])
})
</script>

<template>
	<div v-if="pageData">
		<PageBreadCrumb :breadcrumbs="breadcrumbs" class="py-16px lg:pt-32px" />

		<!-- Hero Section -->
		<section class="relative overflow-hidden bg-o-50">
			<div
				class="pointer-events-none absolute inset-0 hidden lg:block"
				aria-hidden="true"
			>
				<div
					class="absolute h-[488px] w-[488px] -translate-x-[680px] translate-y-[0px] place-self-center rounded-[50%] bg-o-75"
				></div>
				<div
					class="absolute h-[523px] w-[523px] -translate-x-[300px] translate-y-[630px] place-self-center rounded-[50%] bg-o-75"
				></div>
				<div
					class="absolute h-[144px] w-[144px] translate-x-[400px] translate-y-[160px] place-self-center rounded-[50%] bg-o-75"
				></div>
				<div
					class="absolute h-[517px] w-[517px] translate-x-[800px] translate-y-[330px] place-self-center rounded-[50%] bg-o-75"
				></div>
			</div>

			<div
				class="bs-container relative flex flex-col items-center gap-24px px-20px py-32px lg:gap-32px lg:py-72px"
			>
				<!-- Header Content -->
				<div class="flex w-full flex-col gap-12px">
					<h1
						class="whitespace-pre-line text-center text-n-950"
						:class="isLarge ? 'typography-h1-str' : 'typography-h2-str'"
					>
						{{ localizedPageContent.hero.title }}
					</h1>
					<p
						class="text-center text-n-950"
						:class="isLarge ? 'typography-b1-reg' : 'typography-s1-reg'"
					>
						{{ pageData.hero_section.description }}
					</p>
				</div>

				<!-- CTA Button and Discount Info -->
				<div
					v-if="!isLoginGlobal"
					class="flex w-full flex-col items-center gap-16px lg:w-auto"
				>
					<BaseButton
						variant="primary"
						size="large"
						class="w-full lg:w-auto"
						@click="handleRegisterClick"
					>
						{{ localizedPageContent.hero.primaryBtn }}
					</BaseButton>
					<div
						v-if="pageData.hero_section.show_welcome_credit_text"
						class="typography-h5-str text-center text-t-700"
					>
						{{ localizedPageContent.hero.discountInfo }}
					</div>
				</div>

				<!-- Stats Grid -->
				<div
					class="grid w-full grid-cols-2 gap-16px lg:grid-cols-4 lg:gap-20px"
				>
					<div
						v-for="(stat, index) in heroStats"
						:key="index"
						class="flex flex-col items-center justify-center gap-8px rounded-[16px] bg-white p-16px shadow-[0px_4px_20px_0px_rgba(25,28,31,0.06)] lg:gap-20px lg:p-20px"
					>
						<div
							class="text-center text-o-500"
							:class="isLarge ? 'typography-h1-str' : 'typography-h2-str'"
						>
							{{ stat.value }}
						</div>
						<div
							class="text-center text-n-950"
							:class="isLarge ? 'typography-h5-str' : 'typography-s1-str'"
						>
							{{ stat.description }}
						</div>
					</div>
				</div>
			</div>
		</section>

		<!-- Comparison Table Section -->
		<section
			v-if="localizedPageContent.comparison && comparisonRows.length"
			class="bs-container py-32px lg:py-72px"
		>
			<div class="col-span-12">
				<!-- Section Header -->
				<div class="mb-24px flex flex-col gap-8px lg:mb-32px lg:items-center">
					<h2
						class="text-center text-n-950"
						:class="isLarge ? 'typography-h2-str' : 'typography-h3-str'"
					>
						{{ localizedPageContent.comparison.title }}
					</h2>
					<p
						class="text-center text-n-500"
						:class="isLarge ? 'typography-s1-reg' : 'typography-s2-reg'"
					>
						{{ localizedPageContent.comparison.description }}
					</p>
				</div>

				<!-- Table Container - Mobile with horizontal scroll -->
				<div
					class="relative overflow-hidden rounded-[16px] lg:ml-[20px] lg:overflow-visible lg:border lg:border-n-100"
				>
					<!-- Mobile: Scrollable container -->
					<div
						class="lg:overflow-visible"
						:class="!isLarge ? 'overflow-x-auto' : ''"
					>
						<table class="w-full border-collapse">
							<!-- Table Header -->
							<thead>
								<tr>
									<!-- Fixed column header (empty) -->
									<th
										class="sticky left-0 z-[1] min-w-[120px] bg-n-50 lg:static lg:z-0 lg:rounded-tl-[16px]"
									></th>
									<!-- Scrollable headers -->
									<th
										v-for="(header, index) in localizedPageContent.comparison
											.headers"
										:key="index"
										class="bg-n-50 p-12px lg:p-20px lg:px-24px"
										:class="[
											isLarge ? 'min-w-[240px]' : 'min-w-[160px]',
											index ===
											localizedPageContent.comparison.headers.length - 1
												? 'lg:rounded-tr-[16px]'
												: '',
										]"
									>
										<div
											class="text-n-950"
											:class="
												isLarge ? 'typography-s1-str' : 'typography-s2-str'
											"
										>
											{{ header }}
										</div>
									</th>
								</tr>
							</thead>

							<!-- Table Body -->
							<tbody>
								<tr
									v-for="(row, rowIndex) in comparisonRows"
									:key="rowIndex"
									:class="[
										row.isHighlight ? 'bg-o-75' : 'bg-white',
										rowIndex < comparisonRows.length - 1
											? 'border-b border-dashed border-n-100'
											: '',
									]"
								>
									<!-- Fixed column - Company name -->
									<td
										class="sticky left-0 z-[1] p-12px lg:relative lg:z-0 lg:px-24px lg:py-20px"
										:class="[
											row.isHighlight
												? 'border-b border-t border-o-500 bg-o-75'
												: 'bg-white',
											rowIndex === comparisonRows.length - 1
												? 'lg:rounded-bl-[16px]'
												: '',
										]"
									>
										<div
											class="text-n-950"
											:class="[
												{
													'text-o-500': row.isHighlight,
													'typography-s1-str': isLarge,
													'typography-s2-reg': !isLarge,
													'typography-s2-str': !isLarge && row.isHighlight,
												},
											]"
										>
											{{ row.company }}
										</div>
										<!-- Orange highlight bar for Buy&Ship on desktop -->
										<div
											v-if="row.isHighlight"
											class="absolute left-0 top-0 h-full w-[6px] bg-o-500 lg:-left-[20px] lg:w-[20px] lg:rounded-l-[12px]"
										></div>
									</td>

									<!-- Scrollable data cells -->
									<td
										v-for="(cell, cellIndex) in row.data"
										:key="cellIndex"
										class="p-12px lg:p-20px lg:px-24px"
										:class="[
											isLarge ? 'min-w-0' : 'min-w-[160px]',
											row.isHighlight ? 'border-b border-t border-o-500' : '',
											row.isHighlight && cellIndex === row.data.length - 1
												? 'border-r'
												: '',
											rowIndex === comparisonRows.length - 1 &&
											cellIndex === row.data.length - 1
												? 'lg:rounded-br-[16px]'
												: '',
										]"
									>
										<div
											class="text-n-950"
											:class="[
												{
													'typography-s1-str': isLarge,
													'typography-s2-reg': !isLarge,
													'typography-s2-str': !isLarge && row.isHighlight,
												},
											]"
										>
											{{ cell }}
										</div>
									</td>
								</tr>
							</tbody>
						</table>
					</div>
				</div>

				<!-- CTA Button -->
				<div class="mt-24px flex justify-center lg:mt-32px">
					<a :href="localizedPageContent.comparison.buttonLink" target="_blank">
						<BaseButton
							variant="secondary-orange"
							size="medium"
							class="w-full lg:w-auto"
							@click="handleComparisonButtonClick"
						>
							{{ localizedPageContent.comparison.buttonText }}
						</BaseButton>
					</a>
				</div>
			</div>
		</section>

		<!-- Featured Brands Section -->
		<FeaturedBrandsSection
			v-if="localizedPageContent.brands && pageData?.id"
			:title="localizedPageContent.brands.title"
			:description="localizedPageContent.brands.description"
			:country-id="pageData.id"
			:button-text="localizedPageContent.brands.buttonText"
			:button-link="localizedPageContent.brands.buttonLink"
		/>

		<!-- Community Posts Section -->
		<section
			v-if="localizedPageContent.communityPosts && isProductSharingEnable"
			class="bs-container py-32px lg:py-72px"
		>
			<div class="col-span-12 flex flex-col gap-24px lg:gap-32px">
				<div class="flex flex-col items-center gap-8px">
					<h2
						class="text-center text-n-950"
						:class="isLarge ? 'typography-h2-str' : 'typography-h3-str'"
					>
						{{ localizedPageContent.communityPosts.title }}
					</h2>

					<p
						class="text-center text-n-500"
						:class="isLarge ? 'typography-s1-reg' : 'typography-s2-reg'"
					>
						{{ localizedPageContent.communityPosts.description }}
					</p>
				</div>

				<ProductSharingPostList
					ref="postListRef"
					class="[&>div]:!p-0"
					:column-count="columnCount"
					:post-per-page="4"
					:text="localizedPageContent.postListText"
					:local-currency-info="localCurrencyInfo"
					is-shipping-global
					@post-item-like="setPostActions"
				/>

				<a
					class="lg:mx-auto lg:flex"
					:href="localizedPageContent.communityPosts.buttonLink"
					target="_blank"
				>
					<BaseButton
						variant="secondary-orange"
						size="medium"
						class="w-full lg:w-auto"
						@click="handleCommunityPostsButtonClick"
					>
						{{ localizedPageContent.communityPosts.buttonText }}
					</BaseButton>
				</a>
			</div>
		</section>

		<!-- Combined Flow Section -->
		<section class="bg-o-50 [&_button]:w-full lg:[&_button]:w-auto">
			<CombinedFlowSection
				v-if="localizedPageContent.combinedFlow"
				:is-logged-in="isLoginGlobal"
				:content="localizedPageContent.combinedFlow"
				:ga-page-name="GaGeneralPageName.ShippingGlobal"
				ga-button-name="Signup - Step Guides"
			/>
		</section>

		<!-- FAQ Section -->
		<section
			v-if="localizedPageContent.faq && faqContent.faqItems.length"
			class="bs-container py-32px lg:py-72px"
		>
			<div class="col-span-12">
				<h2
					class="mb-24px text-center text-n-950 lg:mb-32px"
					:class="isLarge ? 'typography-h2-str' : 'typography-h3-str'"
				>
					{{ localizedPageContent.faq.sectionTitle }}
				</h2>

				<ShipmentProtectionFAQ
					theme="shipping-global"
					:content="faqContent"
					:is-show-left-section="false"
				/>
			</div>
		</section>

		<!-- Latest News Section -->
		<section
			v-if="localizedPageContent.latestNews && latestNewsPosts.length"
			class="bg-o-50 py-32px lg:py-72px"
		>
			<div class="bs-container">
				<div
					class="col-span-12 flex flex-col items-center gap-24px lg:gap-32px"
				>
					<!-- Title -->
					<h2 class="typography-h3-str w-full text-center text-n-950">
						{{ localizedPageContent.latestNews.title }}
					</h2>

					<!-- Posts List -->
					<div
						class="no-scrollbar -mx-20px flex w-screen gap-20px overflow-x-auto px-20px lg:mx-0 lg:grid lg:w-full lg:grid-cols-4 lg:overflow-visible lg:px-0"
					>
						<a
							v-for="post in latestNewsPosts"
							:key="post.url"
							:href="post.url"
							class="group flex w-[285px] flex-shrink-0 flex-col overflow-hidden rounded-lg border border-n-100 bg-white pb-24px transition-all duration-300 [transition-timing-function:cubic-bezier(0.25,0.8,0.25,1)] lg:min-w-0"
							@click="handleLatestNewsPostClick(post.title)"
						>
							<!-- Image Container -->
							<div class="relative h-[174px] w-full">
								<Skeleton
									v-if="!imageLoadedStates[post.image]"
									class="absolute inset-0 h-full w-full"
								/>

								<img
									:src="post.image"
									:alt="post.title"
									class="h-full w-full object-cover transition-opacity duration-300"
									:class="{ 'opacity-0': !imageLoadedStates[post.image] }"
									loading="lazy"
									@load="onImageLoad(post.image)"
									@error="onImageError(post.image)"
								/>
							</div>

							<!-- Content -->
							<div class="px-16px pt-16px">
								<h3
									class="line-clamp-2 text-ellipsis font-['Noto_Sans_TC'] text-[16px] font-medium leading-[1.5em] tracking-[0.021875em] text-n-950"
								>
									{{ post.title }}
								</h3>
							</div>
						</a>
					</div>
				</div>
			</div>
		</section>

		<!-- Payment Methods Section -->
		<section
			v-if="localizedPageContent.paymentsSection"
			class="bs-container py-32px lg:py-acquaintances"
		>
			<div
				class="col-span-12 flex flex-col items-center justify-center gap-24px lg:gap-good-friends"
			>
				<h2 class="typography-h5-str w-full text-center text-n-400">
					{{ localizedPageContent.paymentsSection.title }}
				</h2>

				<div
					class="flex flex-wrap items-center justify-center gap-acquaintances lg:gap-60px"
				>
					<img
						v-for="(image, index) in localizedPageContent.paymentsSection
							.imageList"
						:key="index"
						:src="image"
						:alt="`Payment method ${index + 1}`"
						class="h-[48px] w-[70px] object-contain"
						loading="lazy"
					/>
				</div>
			</div>
		</section>

		<!-- Other Warehouses Section -->
		<section
			v-if="localizedPageContent.warehousesSection && otherWarehouses.length"
			class="bg-n-50 py-32px lg:py-60px"
		>
			<div class="bs-container">
				<div
					class="col-span-12 flex flex-col items-center gap-24px lg:gap-good-friends"
				>
					<h2 class="typography-h5-str w-full text-center text-n-500">
						{{ localizedPageContent.warehousesSection.title }}
					</h2>

					<div class="grid w-full grid-cols-2 gap-20px lg:grid-cols-5">
						<a
							v-for="warehouse in otherWarehouses"
							:key="warehouse.id"
							:href="`${getSiteBaseUrl()}/shipping/from-${warehouse.slug}`"
							class="flex cursor-pointer items-center justify-between gap-8px rounded-[8px] border border-n-75 bg-white p-12px transition-colors hover:bg-n-25 lg:p-16px"
							@click="handleOtherWarehousesClick(warehouse.slug)"
						>
							<div class="flex items-center gap-8px">
								<img
									:src="getFlagIcon(warehouse.id)"
									:alt="`${warehouse.name} flag`"
									class="h-[20px] w-[20px] flex-shrink-0 object-contain lg:h-[24px] lg:w-[24px]"
									loading="lazy"
								/>
								<span
									class="text-n-950"
									:class="isLarge ? 'typography-s1-emp' : 'typography-s2-emp'"
								>
									{{ warehouse.name }}
								</span>
							</div>

							<BaseIcon
								name="chevron_thin"
								class="h-[20px] w-[20px] flex-shrink-0 text-n-300"
							/>
						</a>
					</div>
				</div>
			</div>
		</section>

		<!-- Post Card Dialog -->
		<BsDialogAction
			v-if="isProductSharingEnable"
			v-model="isShowDialogPostDetail"
			:type="DialogType.BottomSheet"
			:close-text="localizedPageContent.postCardTextInfo.dialog.close"
			:is-hide-button-close="false"
			@on-hide="hideDialogPostDetail('dialog')"
		>
			<template #content>
				<BsProductSharingPostCard
					@update-post="setPostActions"
					@back="hideDialogPostDetail('back')"
					:text="localizedPageContent.postCardTextInfo"
					:detail="postDetailCurrent"
					:interest="interestsMap"
					:categories="categoryMap"
					:warehouses="warehouseIdMap"
					:currencies="supportedCurrencies"
					:support-warehouses="buyforyouSupportWarehouse"
					:price-local="priceLocal"
					:price-local-post="priceLocalPost"
					:is-type-modal="true"
					:is-loading="isLoadingPostDetail"
					:local-currency-info="localCurrencyInfo"
					:post-list-text-info="localizedPageContent.postListText"
				></BsProductSharingPostCard>
			</template>
		</BsDialogAction>

		<!-- Snackbar -->
		<Teleport to="body">
			<BsSnackBar
				:content="messages[0]"
				:is-show="isShow"
				@dismiss="dismiss"
			></BsSnackBar>
		</Teleport>

		<!-- Back to top button -->
		<div
			v-show="isShowBackToTopButton"
			class="fixed bottom-[81px] right-20px z-50"
		>
			<BackButton @click="scrollToTop" />
		</div>
	</div>
</template>
